import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ChevronLeft, RotateCw, FileText, Trash2, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, belegeAPI, lieferantenAPI } from '../lib/api';

const STATUS_LABEL = {
  eingang: 'Eingang',
  extracting: 'Extraktion läuft',
  extrahiert: 'Extrahiert',
  vorgeschlagen: 'Vorgeschlagen',
  review: 'Review',
  gebucht: 'Gebucht',
  exportiert: 'Exportiert',
  verworfen: 'Verworfen',
};

// Verworfen ist im Backend nicht hart-locked, aber UX-seitig sperren wir das Form
// (Reaktivierung via "Zurück zu Review"-Button).
const LOCKED = new Set(['extracting', 'gebucht', 'exportiert', 'verworfen']);
const REJECTABLE = new Set(['eingang', 'extrahiert', 'review', 'vorgeschlagen']);

const FORM_FIELDS = [
  'lieferant_id',
  'beleg_datum',
  'rechnungsnummer',
  'betrag_brutto',
  'betrag_netto',
  'ust_betrag',
  'ust_satz',
  'waehrung',
];

function fmtMoney(v) {
  if (v === null || v === undefined || v === '') return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);
}

function diffPayload(current, edited) {
  const out = {};
  for (const k of FORM_FIELDS) {
    const a = current?.[k] ?? null;
    const b = edited?.[k] ?? null;
    // empty-string in form → null on the server (clears field)
    const bNorm = b === '' ? null : b;
    if (String(a ?? '') !== String(bNorm ?? '')) out[k] = bNorm;
  }
  return out;
}

export default function BelegDetail() {
  const { slug, id } = useParams();

  const [beleg, setBeleg] = useState(null);
  const [vorschlag, setVorschlag] = useState([]);
  const [lieferanten, setLieferanten] = useState([]);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fileBlobUrl, setFileBlobUrl] = useState(null);
  const [fileMime, setFileMime] = useState(null);

  const isLocked = beleg ? LOCKED.has(beleg.status) : true;

  // Load beleg + vorschlag + lieferanten
  const refresh = async () => {
    const [b, v, l] = await Promise.all([
      belegeAPI.get(slug, id),
      belegeAPI.vorschlag(slug, id),
      lieferantenAPI.list(slug, { limit: 500 }),
    ]);
    setBeleg(b.data);
    setVorschlag(v.data);
    setLieferanten(l.data);
    const f = {};
    for (const k of FORM_FIELDS) f[k] = b.data[k] ?? '';
    setForm(f);
  };

  useEffect(() => {
    setLoading(true);
    refresh()
      .catch((e) => toast.error(e.response?.data?.detail || 'Beleg konnte nicht geladen werden'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, id]);

  // Poll while extracting/eingang — silently refresh so the user sees the result
  // when the BG extraction task lands.
  useEffect(() => {
    if (!beleg) return undefined;
    if (beleg.status !== 'extracting' && beleg.status !== 'eingang') return undefined;
    const handle = setInterval(async () => {
      try {
        const r = await belegeAPI.get(slug, id);
        setBeleg(r.data);
        // Once it leaves the transient state, also refresh the form + vorschlag
        if (r.data.status !== 'extracting' && r.data.status !== 'eingang') {
          const v = await belegeAPI.vorschlag(slug, id);
          setVorschlag(v.data);
          const f = {};
          for (const k of FORM_FIELDS) f[k] = r.data[k] ?? '';
          setForm(f);
        }
      } catch {
        /* swallow transient network errors */
      }
    }, 3000);
    return () => clearInterval(handle);
    // intentional: only re-arm when status changes, not on every poll-induced beleg update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beleg?.status, slug, id]);

  // Fetch the file as a blob (axios sends auth header), build object URL
  useEffect(() => {
    let url;
    api
      .get(`/mandanten/${slug}/belege/${id}/file`, { responseType: 'blob' })
      .then((r) => {
        url = URL.createObjectURL(r.data);
        setFileBlobUrl(url);
        setFileMime(r.data.type || 'application/octet-stream');
      })
      .catch(() => {
        // file may not exist yet (rare); silent
      });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [slug, id]);

  const dirty = useMemo(() => Object.keys(diffPayload(beleg, form)).length > 0, [beleg, form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async ({ withStatus } = {}) => {
    const patch = diffPayload(beleg, form);
    if (withStatus) patch.status = withStatus;
    if (Object.keys(patch).length === 0) {
      toast('Nichts zu speichern');
      return;
    }
    setBusy(true);
    try {
      await belegeAPI.patch(slug, id, patch);
      toast.success(withStatus ? `Status → ${STATUS_LABEL[withStatus]}` : 'Gespeichert');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const onBuchen = async () => {
    if (!window.confirm('Beleg endgültig buchen? Danach nicht mehr änderbar.')) return;
    setBusy(true);
    try {
      await belegeAPI.buchen(slug, id);
      toast.success('Gebucht');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Buchen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const onReExtract = async () => {
    setBusy(true);
    try {
      await belegeAPI.extract(slug, id);
      toast.success('Re-Extraktion abgeschlossen');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extraktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const onVerwerfen = async () => {
    if (!window.confirm('Beleg verwerfen? Er erscheint danach nicht mehr in den Auswertungen, kann aber reaktiviert werden.')) return;
    setBusy(true);
    try {
      await belegeAPI.patch(slug, id, { status: 'verworfen' });
      toast.success('Verworfen');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verwerfen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const onReaktivieren = async () => {
    setBusy(true);
    try {
      await belegeAPI.patch(slug, id, { status: 'review' });
      toast.success('Zurück in Review');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reaktivierung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="page muted">Lädt…</div>;
  if (!beleg) return <div className="page muted">Beleg nicht gefunden.</div>;

  const isImage = fileMime && fileMime.startsWith('image/');

  return (
    <div className="page detail-page">
      <header className="page-header">
        <div>
          <Link to={`/mandanten/${slug}/belege`} className="back-link">
            <ChevronLeft size={14} /> Belege
          </Link>
          <h1>
            Beleg #{beleg.id}{' '}
            <span className={`badge badge-${beleg.status}`}>
              {STATUS_LABEL[beleg.status] || beleg.status}
            </span>
          </h1>
          <div className="muted">
            Hochgeladen am {format(new Date(beleg.uploaded_at), 'dd.MM.yyyy HH:mm')}
            {beleg.confidence != null && (
              <> · Confidence {(Number(beleg.confidence) * 100).toFixed(0)}%</>
            )}
            {beleg.ki_modell && <> · {beleg.ki_modell}</>}
          </div>
        </div>
      </header>

      <div className="detail-grid">
        {/* --- File preview ------------------------------------------- */}
        <div className="card preview-card">
          {fileBlobUrl ? (
            isImage ? (
              <img src={fileBlobUrl} alt="Beleg" className="preview-img" />
            ) : (
              <iframe src={fileBlobUrl} title="Beleg" className="preview-frame" />
            )
          ) : (
            <div className="muted">
              <FileText size={48} /> Datei wird geladen…
            </div>
          )}
        </div>

        {/* --- Form + Actions ----------------------------------------- */}
        <div className="detail-right">
          <div className="card">
            <h3>Buchungsdaten</h3>
            <div className="form-grid">
              <label>
                Lieferant
                <select
                  value={form.lieferant_id || ''}
                  onChange={(e) => setField('lieferant_id', e.target.value ? Number(e.target.value) : null)}
                  disabled={isLocked}
                >
                  <option value="">— wählen —</option>
                  {lieferanten.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                      {l.ust_id ? ` (${l.ust_id})` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Beleg-Datum
                <input
                  type="date"
                  value={form.beleg_datum || ''}
                  onChange={(e) => setField('beleg_datum', e.target.value)}
                  disabled={isLocked}
                />
              </label>

              <label>
                Rechnungs-Nr
                <input
                  type="text"
                  value={form.rechnungsnummer || ''}
                  onChange={(e) => setField('rechnungsnummer', e.target.value)}
                  disabled={isLocked}
                />
              </label>

              <label>
                USt-Satz (%)
                <input
                  type="number"
                  step="1"
                  value={form.ust_satz ?? ''}
                  onChange={(e) => setField('ust_satz', e.target.value)}
                  disabled={isLocked}
                />
              </label>

              <label>
                Netto
                <input
                  type="number"
                  step="0.01"
                  value={form.betrag_netto ?? ''}
                  onChange={(e) => setField('betrag_netto', e.target.value)}
                  disabled={isLocked}
                />
              </label>

              <label>
                USt-Betrag
                <input
                  type="number"
                  step="0.01"
                  value={form.ust_betrag ?? ''}
                  onChange={(e) => setField('ust_betrag', e.target.value)}
                  disabled={isLocked}
                />
              </label>

              <label>
                Brutto
                <input
                  type="number"
                  step="0.01"
                  value={form.betrag_brutto ?? ''}
                  onChange={(e) => setField('betrag_brutto', e.target.value)}
                  disabled={isLocked}
                />
              </label>

              <label>
                Währung
                <input
                  type="text"
                  value={form.waehrung || 'EUR'}
                  onChange={(e) => setField('waehrung', e.target.value)}
                  disabled={isLocked}
                />
              </label>
            </div>

            {isLocked && (
              <div className="banner banner-info">
                Beleg im Status <b>{STATUS_LABEL[beleg.status]}</b> — keine Änderungen möglich.
              </div>
            )}

            <div className="actions">
              {!isLocked && (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => onSave()}
                    disabled={busy || !dirty}
                  >
                    Speichern
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => onSave({ withStatus: 'vorgeschlagen' })}
                    disabled={busy}
                    title="Speichert die Änderungen und gibt den Beleg zur Buchung frei"
                  >
                    Speichern &amp; Freigeben
                  </button>
                </>
              )}
              {beleg.status === 'vorgeschlagen' && (
                <button className="btn-primary" onClick={onBuchen} disabled={busy}>
                  Buchen
                </button>
              )}
              {beleg.status === 'verworfen' && (
                <button className="btn-secondary" onClick={onReaktivieren} disabled={busy}>
                  <RotateCcw size={14} /> Zurück zu Review
                </button>
              )}
              {beleg.status !== 'extracting' && (
                <button className="btn-text" onClick={onReExtract} disabled={busy} title="KI-Extraktion erneut ausführen">
                  <RotateCw size={14} /> Re-Extrahieren
                </button>
              )}
              {REJECTABLE.has(beleg.status) && (
                <button
                  className="btn-danger"
                  style={{ marginLeft: 'auto' }}
                  onClick={onVerwerfen}
                  disabled={busy}
                  title="Beleg verwerfen — kein Buchungsvorgang"
                >
                  <Trash2 size={14} /> Verwerfen
                </button>
              )}
            </div>
          </div>

          {/* --- Buchungsvorschlag --------------------------------- */}
          {vorschlag.length > 0 && (
            <div className="card">
              <h3>{beleg.status === 'gebucht' || beleg.status === 'exportiert' ? 'Buchung' : 'Buchungsvorschlag'}</h3>
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th>Soll</th>
                    <th>Haben</th>
                    <th>USt-Kennz</th>
                    <th>Betrag</th>
                    <th>Buchungstext</th>
                  </tr>
                </thead>
                <tbody>
                  {vorschlag.map((b) => (
                    <tr key={b.id}>
                      <td>{b.soll_konto}</td>
                      <td>{b.haben_konto}</td>
                      <td>{b.ust_kennz || '—'}</td>
                      <td className="num">{fmtMoney(b.betrag)}</td>
                      <td>{b.buchungstext}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
